<template>
  <div class="pt-4 px-4">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    ></v-progress-linear>

    <div v-if="!loading">
      <v-layout row align-center class="mb-4" wrap>
        <v-flex xs12 sm5>
          <div class="display-1 font-weight-bold">Bus Assignments</div>
          <div class="grey--text mt-1">
            Students assigned to routes, published from route designs or added
            manually
          </div>
        </v-flex>
        <v-flex
          xs12
          sm7
          style="display:flex;align-items:center;justify-content:flex-end;gap:12px"
        >
          <v-select
            v-model="kind"
            :items="kindOptions"
            label="Kind"
            dense
            outlined
            hide-details
            style="max-width:140px"
            @change="onKindChange"
          ></v-select>
          <v-autocomplete
            v-model="selectedTerm"
            :items="terms"
            label="Term"
            dense
            outlined
            hide-details
            style="max-width:160px"
            @change="onTermChange"
          ></v-autocomplete>
          <v-autocomplete
            v-model="selectedRouteIds"
            :items="routeOptions"
            item-text="label"
            item-value="id"
            label="Routes"
            dense
            outlined
            hide-details
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            style="max-width:320px"
          ></v-autocomplete>
          <v-btn
            color="success"
            outline
            @click="$router.push({ name: 'RouteDesigns' })"
          >
            <v-icon left>mdi-map-marker-distance</v-icon>
            Route Designs
          </v-btn>
          <v-btn color="success" :disabled="!selectedTerm" @click="openAdd">
            <v-icon left>mdi-plus</v-icon>
            Add
          </v-btn>
        </v-flex>
      </v-layout>

      <v-card elevation="2">
        <v-card-title class="pt-2">
          <v-btn
            color="error"
            outline
            :disabled="selected.length === 0"
            @click="deleteDialog = true"
          >
            <v-icon left small>mdi-delete</v-icon>
            Remove ({{ selected.length }})
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn
                color="primary"
                outline
                :loading="downloading"
                :disabled="!filteredRows.length"
                v-on="on"
              >
                <v-icon left small>mdi-printer</v-icon>
                Print {{ selected.length ? "selected (" + selected.length + ")" : "" }}
                <v-icon right small>mdi-menu-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-tile @click="download('bus')">
                <v-list-tile-title>Bus report (by stop)</v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="download('class')">
                <v-list-tile-title>Section report</v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="download('labels')">
                <v-list-tile-title>Labels (Avery 5160)</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
          <span class="grey--text ml-3">
            {{ filteredRows.length }} assignment{{
              filteredRows.length === 1 ? "" : "s"
            }}
            · {{ studentCount }} student{{ studentCount === 1 ? "" : "s" }}
          </span>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            clearable
            style="max-width:300px"
          ></v-text-field>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="filteredRows"
          :search="search"
          :pagination.sync="pagination"
          :rows-per-page-items="[25, 50, 100, { text: 'All', value: -1 }]"
          item-key="id"
          select-all
          v-model="selected"
          class="pl-2"
        >
          <template v-slot:items="props">
            <tr>
              <td>
                <v-checkbox
                  v-model="props.selected"
                  color="success"
                  hide-details
                ></v-checkbox>
              </td>
              <td>{{ props.item.studentID }}</td>
              <td>
                <div class="font-weight-medium">{{ props.item.hebName }}</div>
                <div class="caption grey--text">{{ props.item.name }}</div>
              </td>
              <td>{{ props.item.class }}</td>
              <td>{{ props.item.building }}</td>
              <td>
                <div>{{ props.item.routeLabel }}</div>
                <div class="caption grey--text">
                  {{ [props.item.weekDays, props.item.timeStart].filter(Boolean).join(" · ") }}
                </div>
              </td>
              <td>{{ props.item.busNumber }}</td>
              <td>{{ props.item.stopNumber }}</td>
              <td>
                <div v-if="props.item.stopName" class="font-weight-medium">
                  {{ props.item.stopName }}
                </div>
                <div :class="{ 'caption grey--text': props.item.stopName }">
                  {{ props.item.stopAddress || props.item.busAddress }}
                </div>
              </td>
              <td>
                <router-link
                  v-if="props.item.routeDesignID"
                  :to="{
                    name: 'RouteDesignMap',
                    params: { id: String(props.item.routeDesignID) },
                  }"
                  >{{ props.item.routeDesignName || "Route design" }}</router-link
                >
                <span v-else class="grey--text">Manual</span>
              </td>
              <td class="caption">
                {{ formatDate(props.item.updatedAt || props.item.createdAt) }}
                <div class="grey--text">
                  {{ props.item.updatedBy || props.item.createdBy }}
                </div>
              </td>
              <td>
                <v-btn icon small class="ma-0" @click="openEdit(props.item)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            <div class="text-xs-center pa-6 grey--text">
              <v-icon large color="grey lighten-1">mdi-bus-school</v-icon>
              <div class="mt-2">No bus assignments for this filter</div>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <v-dialog v-model="editDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline success white--text pa-3">
          {{ form.id ? "Edit" : "Add" }} Bus Assignment
        </v-card-title>
        <v-card-text class="pt-3">
          <v-autocomplete
            v-model="form.studentID"
            :items="studentItems"
            :search-input.sync="studentSearch"
            :loading="studentSearching"
            :disabled="!!form.id"
            item-text="text"
            item-value="studentID"
            label="Student *"
            placeholder="Type a name or student ID"
            no-filter
            hide-no-data
            outlined
            dense
          ></v-autocomplete>
          <v-autocomplete
            v-model="form.routeID"
            :items="routeItems"
            item-text="label"
            item-value="id"
            label="Route *"
            outlined
            dense
          >
            <template v-slot:item="{ item }">
              <v-list-tile-content>
                <v-list-tile-title class="font-weight-medium">{{
                  routeTitle(item)
                }}</v-list-tile-title>
                <v-list-tile-sub-title>{{
                  routeDetails(item)
                }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-autocomplete>
          <v-layout row wrap>
            <v-flex xs12 sm3 class="pr-2">
              <v-text-field
                v-model.number="form.stopNumber"
                label="Stop #"
                type="number"
                min="1"
                outlined
                dense
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm9>
              <v-text-field
                v-model="form.stopName"
                label="Stop name"
                placeholder="e.g. Lee & Hewes"
                outlined
                dense
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-text-field
            v-model="form.stopAddress"
            label="Stop address"
            hint="Leave blank to use the student's bus address"
            persistent-hint
            outlined
            dense
            class="mb-2"
          ></v-text-field>
          <v-textarea
            v-model="form.notes"
            label="Notes"
            rows="2"
            auto-grow
            outlined
            dense
          ></v-textarea>
          <div v-if="form.routeDesignID" class="caption grey--text">
            Published from route design "{{ form.routeDesignName }}". Republishing
            that design overwrites the route for this student.
          </div>
          <v-alert
            :value="!!formError"
            type="error"
            class="mt-3"
            @input="formError = ''"
          >
            {{ formError }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="pb-3 pr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="editDialog = false">Cancel</v-btn>
          <v-btn
            color="success"
            :loading="saving"
            :disabled="!form.studentID || !form.routeID"
            @click="save"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Remove assignments?</v-card-title>
        <v-card-text>
          Remove {{ selected.length }} selected bus
          {{ selected.length === 1 ? "assignment" : "assignments" }}?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="deleteDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="error" :loading="deleting" @click="doDelete">
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-alert
      :value="!!errorMessage"
      type="error"
      @input="errorMessage = ''"
      class="mt-4"
    >
      {{ errorMessage }}
    </v-alert>
  </div>
</template>

<script>
import {
  getStoredRouteKind,
  setStoredRouteKind,
  routeOptionTitle,
  routeOptionDetails,
} from "../../utils/helpers.js";

export default {
  name: "BusAssignments",
  data() {
    return {
      loading: true,
      terms: [],
      selectedTerm: "",
      kind: "Regular",
      kindOptions: [
        { text: "Regular", value: "Regular" },
        { text: "Day Camp", value: "DayCamp" },
      ],
      routeOptions: [],
      selectedRouteIds: [],
      rows: [],
      selected: [],
      search: "",
      pagination: { rowsPerPage: 50, sortBy: "routeLabel", descending: false },
      headers: [
        { text: "Student ID", value: "studentID" },
        { text: "Name", value: "hebName" },
        { text: "Class", value: "class" },
        { text: "Building", value: "building" },
        { text: "Route", value: "routeLabel" },
        { text: "Bus", value: "busNumber" },
        { text: "Stop #", value: "stopNumber" },
        { text: "Stop", value: "stopName" },
        { text: "Source", value: "routeDesignName" },
        { text: "Updated", value: "updatedAt" },
        { text: "", value: "actions", sortable: false },
      ],
      editDialog: false,
      saving: false,
      form: {},
      formError: "",
      studentItems: [],
      studentSearch: null,
      studentSearching: false,
      deleteDialog: false,
      deleting: false,
      downloading: false,
      errorMessage: "",
    };
  },
  computed: {
    filteredRows() {
      if (!this.selectedRouteIds.length) return this.rows;
      return this.rows.filter(
        (r) => this.selectedRouteIds.indexOf(r.routeID) !== -1
      );
    },
    routeItems() {
      const items = this.routeOptions.slice();
      if (
        this.form.routeID &&
        !items.some((r) => r.id === this.form.routeID)
      ) {
        const row = this.rows.find((r) => r.routeID === this.form.routeID);
        items.unshift({
          id: this.form.routeID,
          label: row ? row.routeLabel : "Route " + this.form.routeID,
        });
      }
      return items;
    },
    studentCount() {
      const ids = {};
      this.filteredRows.forEach((r) => (ids[r.studentID] = true));
      return Object.keys(ids).length;
    },
  },
  watch: {
    studentSearch(val) {
      clearTimeout(this._studentSearchTimer);
      if (!val || val.length < 2 || this.form.id) return;
      const selected = this.studentItems.find(
        (s) => s.studentID === this.form.studentID
      );
      if (selected && selected.text === val) return;
      this._studentSearchTimer = setTimeout(() => this.searchStudents(val), 300);
    },
  },
  async created() {
    try {
      this.loading = true;
      this.kind = getStoredRouteKind();
      this.terms = (await this.$http.get("api/allStudents/getRegTerms")) || [];
      const stored = localStorage.getItem("busAssignments.selectedTerm");
      this.selectedTerm =
        stored && this.terms.indexOf(stored) !== -1
          ? stored
          : this.terms.length
          ? this.terms[this.terms.length - 1]
          : "";
      await this.loadTermData();
    } catch (e) {
      console.error(e);
      this.errorMessage = "Failed to load bus assignments";
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async onTermChange() {
      if (this.selectedTerm) {
        localStorage.setItem("busAssignments.selectedTerm", this.selectedTerm);
      }
      this.selectedRouteIds = [];
      await this.loadTermData();
    },
    async onKindChange() {
      setStoredRouteKind(this.kind);
      this.selectedRouteIds = [];
      this.routeOptions = await this.loadRouteOptions();
    },
    loadRouteOptions() {
      return this.$http.get(
        "api/busAssignment/getRouteOptions?kind=" +
          encodeURIComponent(this.kind || "")
      );
    },
    async loadTermData() {
      const term = encodeURIComponent(this.selectedTerm || "");
      const [routes, rows] = await Promise.all([
        this.loadRouteOptions(),
        this.$http.get("api/busAssignment/getList?term=" + term),
      ]);
      this.routeOptions = routes || [];
      this.rows = rows || [];
      this.selected = [];
    },
    async loadRows() {
      this.rows =
        (await this.$http.get(
          "api/busAssignment/getList?term=" +
            encodeURIComponent(this.selectedTerm || "")
        )) || [];
      this.selected = [];
    },
    routeTitle: routeOptionTitle,
    routeDetails: routeOptionDetails,
    formatDate(val) {
      if (!val) return "";
      return new Date(val).toLocaleDateString();
    },
    studentText(s) {
      return [s.studentID, s.hebName || s.name, s.class]
        .filter(Boolean)
        .join(" · ");
    },
    async searchStudents(q) {
      this.studentSearching = true;
      try {
        const list =
          (await this.$http.get(
            "api/busAssignment/searchStudents?term=" +
              encodeURIComponent(this.selectedTerm) +
              "&q=" +
              encodeURIComponent(q)
          )) || [];
        this.studentItems = list.map((s) => ({
          ...s,
          text: this.studentText(s),
        }));
      } catch (e) {
        console.error(e);
      } finally {
        this.studentSearching = false;
      }
    },
    openAdd() {
      this.form = {
        id: 0,
        studentID: null,
        routeID: this.selectedRouteIds.length === 1 ? this.selectedRouteIds[0] : null,
        stopNumber: null,
        stopName: "",
        stopAddress: "",
        notes: "",
      };
      this.studentItems = [];
      this.studentSearch = null;
      this.formError = "";
      this.editDialog = true;
    },
    openEdit(item) {
      this.form = { ...item };
      this.studentItems = [
        {
          studentID: item.studentID,
          text: this.studentText(item),
        },
      ];
      this.formError = "";
      this.editDialog = true;
    },
    async save() {
      this.saving = true;
      this.formError = "";
      try {
        await this.$http.post(
          "api/busAssignment/save",
          {
            id: this.form.id || 0,
            studentID: this.form.studentID,
            term: this.selectedTerm,
            routeID: this.form.routeID,
            stopNumber:
              this.form.stopNumber === "" || this.form.stopNumber == null
                ? null
                : this.form.stopNumber,
            stopName: this.form.stopName || null,
            stopAddress: this.form.stopAddress || null,
            stopLat: this.form.stopLat || null,
            stopLng: this.form.stopLng || null,
            notes: this.form.notes || null,
          },
          { notifyError: false }
        );
        this.editDialog = false;
        await this.loadRows();
      } catch (e) {
        this.formError = (e && e.message) || "Failed to save";
      } finally {
        this.saving = false;
      }
    },
    async download(kind) {
      this.downloading = true;
      try {
        const response = await this.$http.post(
          "api/busAssignment/download",
          {
            term: this.selectedTerm,
            kind,
            routeIDs: this.selectedRouteIds.length ? this.selectedRouteIds : null,
            assignmentIDs: this.selected.length
              ? this.selected.map((r) => r.id)
              : null,
          },
          { responseType: "blob" }
        );
        const names = { bus: "BusReport", class: "SectionBusReport", labels: "BusLabels" };
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(
          new Blob([response], { type: "application/pdf" })
        );
        link.download = names[kind] + "_" + this.selectedTerm + ".pdf";
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (e) {
        console.error(e);
      } finally {
        this.downloading = false;
      }
    },
    async doDelete() {
      this.deleting = true;
      try {
        await this.$http.post(
          "api/busAssignment/delete",
          this.selected.map((r) => r.id)
        );
        this.deleteDialog = false;
        await this.loadRows();
      } catch (e) {
        console.error(e);
        this.errorMessage = "Failed to remove assignments";
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
